// ╔══════════════════════════════════════════════════════════════════════╗
// ║ DUPLICATED 2026-06-12 under Railway outage (server deploy root       ║
// ║ excludes /src). Single-source relocation scheduled Sunday.           ║
// ║                                                                      ║
// ║ ANY curation edit to MAIN_CARD_COMP_IDS must be made in BOTH:        ║
// ║   • src/lib/mainCard.js     (this file — client picker + odds.js)    ║
// ║   • server/lib/mainCard.js  (server grader backstop path)            ║
// ║                                                                      ║
// ║ until the Sunday relocation lands. See HANDOFF "Railway outage       ║
// ║ 2026-06-12" entry for context and the relocation plan.               ║
// ╚══════════════════════════════════════════════════════════════════════╝
//
// Single source of truth for UFC Main-Card-Only mode eligibility.
//
// This module is intentionally plain ES — no React, no Vite-specific
// syntax, no browser-only globals — so it can be imported by:
//   • client code (src/lib/odds.js, src/components/PickemMode.jsx)
//   • server code (server/index.js grader backstop)
//
// Importing the same file from both ends means MAIN_CARD_COMP_IDS lives
// in exactly one place; a curation update is one edit, not two.
//
// Used ONLY by contest_type === 'pick_em_main_card_only' code paths.
// NEVER consulted for the existing UFC:pick_em mode. NEVER influences
// tier or points computation in getUFCFights. The override at
// ALL_MAIN_CARD_EVENT_IDS (in odds.js) is untouched — full-pickem
// Freedom 250 pools score byte-identical because no code they
// execute reads anything from this file.
//
// SHAPE: Map<eventIdString, Set<fightIdString>>.
//
// Fail-closed semantic: an event missing from the map (or present with
// an empty Set) means "main card not curated yet" — the picker
// surfaces that explicitly via hasMainCardDefinition and the
// eligibility check fails closed. This prevents a misconfigured pool
// from silently allowing all fights.
//
// CURATION: operator-supplied per event. Get fight IDs from:
//   curl https://wannabet-production-a85d.up.railway.app/api/espn/ufc/events
//
// Each main-card-only event needs one Map entry mapping eventId →
// Set of comp.id strings for the fights that broadcast on the main
// card. Inline comment lists the broadcast order (latest = headliner)
// for human readability; the Set itself is order-agnostic.

export const MAIN_CARD_COMP_IDS = new Map([
  // UFC Freedom 250 — 2026-06-14 (Sunday), Washington D.C., The Anthem
  // Operator declared 2026-06-11 PM: this card is SPECIAL — all seven
  // fights broadcast as the main card, no prelims. The MCO mode on this
  // event is therefore equivalent to full pick'em (every fight is
  // eligible). The override at ALL_MAIN_CARD_EVENT_IDS (odds.js) handles
  // the tier/points side; this entry handles MCO eligibility. Both
  // surfaces report the same 7-fight slate for Freedom 250.
  ['600058854', new Set([
    '401863575',  // Topuria vs Gaethje   — headliner (5R)
    '401863576',  // Pereira vs Gane      — co-main (5R)
    '401863577',  // O'Malley vs Zahabi
    '401868926',  // Hokit vs Lewis
    '401863578',  // Ruffy vs Chandler
    '401863579',  // Nickal vs Daukaus
    '401863580',  // Lopes vs Garcia
  ])],
])

/**
 * Eligibility check for the new Main-Card-Only mode. Fails closed:
 * returns false when the event isn't curated, when the event's Set is
 * empty, or when the fightId isn't in that event's main-card Set.
 *
 * Only consulted on contest_type === 'pick_em_main_card_only' code
 * paths. The regular UFC:pick_em path NEVER calls this.
 *
 * @param {string|number} eventId  ESPN event id (group.ufc_config.eventId)
 * @param {string|number} fightId  ESPN comp.id for the fight
 * @returns {boolean}
 */
export function isMainCardEligible(eventId, fightId) {
  if (!eventId || !fightId) return false
  const set = MAIN_CARD_COMP_IDS.get(String(eventId))
  if (!set || set.size === 0) return false   // fail closed
  return set.has(String(fightId))
}

/**
 * Whether the event has a curated main-card definition. Distinct from
 * isMainCardEligible: this answers "should the picker render normally
 * vs show the 'not yet defined' message?" without iterating fight IDs.
 *
 * @param {string|number} eventId
 * @returns {boolean}
 */
export function hasMainCardDefinition(eventId) {
  if (!eventId) return false
  const set = MAIN_CARD_COMP_IDS.get(String(eventId))
  return !!set && set.size > 0
}
